/**
 * taxToolbox.js — catalog-driven Blockly toolbox for the Tax Calculate block editor (S1-P7, task 2-C).
 *
 * PURE DATA. This module builds a Blockly `categoryToolbox` JSON object and registers the
 * dynamic-category flyout callbacks. It has **no top-level DOM access** and loads headless in
 * node, so the `.mjs` unit tests can call `buildToolbox(ctx)` directly.
 *
 * ── Import discipline ────────────────────────────────────────────────────────────────
 * It does NOT import Blockly at all: `buildToolbox` returns pure JSON, and
 * `registerToolboxCallbacks(workspace, ctx)` calls methods on the *passed* workspace. The only
 * import is `TAX_BLOCK_TYPES` from `./taxBlocks.js` (task 2-A) — referenced (never duplicated) so
 * block-type drift is impossible; importing it also headless-registers the block definitions.
 *
 * Category colours come from the `categorystyle` names defined in `taxTheme.js` (task 2-B) — this
 * module references those names and never hard-codes a colour.
 *
 * ── Public contract (for the view, task 5-A) ─────────────────────────────────────────
 *   buildToolbox(ctx)                       → { kind:'categoryToolbox', contents:[…10 categories…] }
 *   registerToolboxCallbacks(workspace,ctx) → wires the dynamic categories (call AFTER Blockly.inject,
 *                                             BEFORE a dynamic category is opened).
 *   filterFlyoutItems(items, query)         → helper for a 5-A search field (filters flyout JSON items).
 *   describeVariable(name, ctx)             → the live gloss/tooltip for a tax_variable NAME (the view
 *                                             re-derives canvas blocks with it — nothing is persisted).
 *   CATEGORY_STYLES / TOOLBOX_CALLBACK_KEYS → the categorystyle + custom-callback name maps.
 *
 * ── ctx shape (all optional; empty ctx renders every category) ───────────────────────
 *   {
 *     personalInfo: string[] | {name,description?}[],   // Personal Data category (static)
 *     globals:      string[] | {name,description?}[],   // Globals category (static)
 *     rules:        ({ruleCode,label?}|string)[],       // fallback snapshot for TAX_RULES
 *     constants:    ({constKey,constValue?}|string)[],  // fallback snapshot for CONSTANTS
 *     outputVars:   string[] | {name}[]                 // fallback snapshot for OUTPUT_VARS;
 *                 | {outputVar,label?}[],               //   FORMULA_REFS reads `label` as the ชื่อสูตร
 *     getRules?:      () => rules[],             // live getters (preferred by the callbacks)
 *     getConstants?:  () => constants[],
 *     getOutputVars?: () => outputVars[],
 *     getSearchQuery?:() => string,             // for the optional TAX_SEARCH category
 *     includeSearchCategory?: boolean,          // opt-in: prepend a 🔍 search category (default off)
 *   }
 */
import { TAX_BLOCK_TYPES } from './taxBlocks.js'

// ── categorystyle names (from taxTheme.js, task 2-B) — never hard-code colours here ──
export const CATEGORY_STYLES = {
  define: 'output_category',
  taxRules: 'tax_rules_category',
  personal: 'personal_category',
  globals: 'globals_category',
  constants: 'constants_category',
  number: 'number_category',
  operators: 'operators_category',
  compare: 'compare_category',
  functions: 'functions_category',
  bracket: 'bracket_category',
  paths: 'paths_category',
}

// ── dynamic-category callback keys (toolbox `custom` ⇄ registerToolboxCategoryCallback) ──
export const TOOLBOX_CALLBACK_KEYS = {
  outputVars: 'OUTPUT_VARS', // the "define" category (new formula + sibling output-var chaining refs)
  formulaRefs: 'FORMULA_REFS', // per-formula editor: OTHER formulas in this calc version, as variables
  taxRules: 'TAX_RULES',
  constants: 'CONSTANTS',
  formulaConstants: 'FORMULA_CONSTANTS', // per-formula editor: existing version constants only (read-only)
  search: 'TAX_SEARCH',
}

// tax_variable `source` tags — must match taxBlocks.js SOURCE_STYLE keys (2-A).
const SRC = {
  ruleInput: 'RuleInput',
  ruleRate: 'RuleRate',
  personalInfo: 'PersonalInfo',
  globals: 'Globals',
  outputVar: 'OutputVar',
  constant: 'Constant',
}

// ── description prefixes (the short gloss shown ON a tax_variable block) ──────────────
const DESC = {
  amount: 'จำนวนเงิน',
  rate: 'อัตราเหมา',
  formula: 'สูตร',
  constant: 'ค่าคงที่',
}

// ── drift guard: every emitted block type must exist in taxBlocks' TAX_BLOCK_TYPES ──
function T(type) {
  if (!TAX_BLOCK_TYPES.includes(type)) {
    throw new Error(`taxToolbox: unknown block type "${type}" — not in TAX_BLOCK_TYPES (task 2-A drift?)`)
  }
  return type
}

// ── flyout-item builders (plain JSON — the shared block-serialization shape) ──────────
function blockItem(type, extra) {
  return { kind: 'block', type: T(type), ...(extra || {}) }
}
function labelItem(text) {
  return { kind: 'label', text: String(text) }
}
/**
 * Prefilled tax_variable — fields.NAME + extraState.source so 2-A's loadExtraState restores the
 * badge/style. `desc` rides in extraState (NOT `fields`) because RULECTX is a field_label, which is
 * non-serializable: a `fields: { RULECTX }` entry would be silently dropped.
 *
 * NAME is a fixed label on the block (not typeable), so THIS is the authoring path for a variable
 * name: every name offered here must exist in the version catalog — a name the flyout does not offer
 * is a name the user cannot reach.
 * @param {string} name
 * @param {string} source
 * @param {string} [desc] short gloss; omitted when empty so no block ever repeats its own NAME
 */
function variableItem(name, source, desc) {
  const extraState = { source }
  const d = String(desc == null ? '' : desc).trim()
  if (d) extraState.desc = d
  return { kind: 'block', type: T('tax_variable'), fields: { NAME: String(name) }, extraState }
}
/**
 * Prefilled tax_path — the CANONICAL path text rides in `extraState.path` (NOT `fields`), because
 * tax_path's PATH label is a non-serializable field_label showing the derived `a ▸ b[] ▸ c` spelling.
 * Same division as variableItem's `desc`: what compiles is stored, what is read is derived.
 *
 * The PATH label is fixed text, so — exactly like a variable NAME and a Σ SCOPE — the flyout is the
 * ONLY authoring path: a path this category does not offer is a path the author cannot reach. That is
 * deliberate. A typeable path box could only ever author one that does not exist in the form JSON,
 * and a path that resolves to nothing contributes 0 with no error anywhere.
 * @param {{path:string, label?:string, description?:string}|string} p
 */
function pathItem(p) {
  const path = String((p && typeof p === 'object' ? (p.path || p.name || '') : p) || '').trim()
  const extraState = { path, source: 'Path' }
  const d = String((p && typeof p === 'object' ? (p.description || p.label || '') : '') || '').trim()
  if (d) extraState.desc = d
  return { kind: 'block', type: T('tax_path'), extraState }
}

/**
 * The paths category. Empty is the NORMAL first state (the caller has to supply the form's shape), so
 * it says what to do rather than rendering an empty flyout that reads as a broken feature.
 */
function pathCategory(name, paths) {
  const list = (Array.isArray(paths) ? paths : [])
    .map((x) => (x && typeof x === 'object' ? x : { path: x }))
    .filter((x) => String(x.path || x.name || '').trim() !== '')
  return {
    kind: 'category',
    name,
    categorystyle: CATEGORY_STYLES.paths,
    contents: list.length
      ? [labelItem('[] = ทุกรายการในชั้นนั้น (เส้นทางที่ขึ้นต้นเหมือนกันวนพร้อมกัน)')].concat(list.map(pathItem))
      : [labelItem('ยังไม่มีเส้นทางข้อมูลของแบบฟอร์มนี้')],
  }
}

/** Prefilled tax_constant — compiles to CONSTKEY; CONSTVALUE persisted (never inlined). */
function constantItem(key, value) {
  const fields = { CONSTKEY: String(key || 'CONST_KEY') }
  if (value != null && value !== '' && Number.isFinite(Number(value))) fields.CONSTVALUE = Number(value)
  return { kind: 'block', type: T('tax_constant'), fields }
}

// ── normalizers (accept plain strings, {name}, or DTO-cased objects) ──────────────────
function normalizeNames(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((x) => (x && typeof x === 'object' ? x.name || x.Name || '' : x))
    .map((s) => String(s == null ? '' : s).trim())
    .filter(Boolean)
}
/**
 * Like {@link normalizeNames} but keeps each variable's catalog gloss (TaxVariableDto.Description).
 * The description is whatever the backend ships for that variable — the frontend does not own these
 * names, so it passes the gloss through verbatim and invents nothing when there is none.
 */
function normalizeVars(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((x) => {
      if (!x) return null
      if (typeof x === 'string') return { name: x.trim(), description: '' }
      return {
        name: String(x.name || x.Name || '').trim(),
        description: String(x.description || x.Description || '').trim(),
      }
    })
    .filter((v) => v && v.name)
}
function normalizeRules(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((r) => {
      if (!r) return null
      if (typeof r === 'string') return { ruleCode: r.trim(), label: '' }
      return {
        ruleCode: String(r.ruleCode || r.RuleCode || '').trim(),
        label: String(r.label || r.Label || '').trim(),
      }
    })
    .filter((r) => r && r.ruleCode)
}
function normalizeConstants(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((c) => {
      if (!c) return null
      if (typeof c === 'string') return { constKey: c.trim(), constValue: null }
      const constValue = c.constValue != null ? c.constValue : c.ConstValue != null ? c.ConstValue : null
      return { constKey: String(c.constKey || c.ConstKey || c.key || '').trim(), constValue }
    })
    .filter((c) => c && c.constKey)
}

/** Read a catalog list from ctx: prefer the live getter, fall back to the static snapshot array. */
function readCtx(ctx, getter, staticKey) {
  if (!ctx) return []
  if (typeof ctx[getter] === 'function') {
    try {
      return ctx[getter]() || []
    } catch (_err) {
      return []
    }
  }
  return ctx[staticKey] || []
}

// ──────────────────────────────────────────────────────────────────────────────────────
//  Descriptions — the short gloss a tax_variable wears, so a block is never just an opaque
//  identifier. ONE source of truth: the flyout seeds it via extraState.desc, and the view
//  re-derives the SAME text for blocks already on the canvas (nothing is persisted).
// ──────────────────────────────────────────────────────────────────────────────────────
const EMPTY_DESC = { desc: '', tooltip: '' }

/** A rule's human name: its Thai label, or '' when absent or merely repeating the code. */
function ruleLabelOf(r) {
  const label = String((r && r.label) || '').trim()
  const code = String((r && r.ruleCode) || '').trim()
  return label && label.toLowerCase() !== code.toLowerCase() ? label : ''
}
/** Flyout heading for a rule — the Thai label, else the code. NEVER "CODE · CODE". */
function ruleHeading(r) {
  return ruleLabelOf(r) || String((r && r.ruleCode) || '').trim()
}
/** A formula's human name: its ชื่อสูตร, or '' when it just repeats the OutputVar (a backfilled Name). */
function formulaNameOf(ref) {
  const name = String((ref && ref.label) || '').trim()
  const ov = String((ref && ref.outputVar) || '').trim()
  return name && name.toLowerCase() !== ov.toLowerCase() ? name : ''
}
function withLabel(prefix, label) {
  return label ? `${prefix} · ${label}` : prefix
}

function ruleAmountDesc(r) {
  const label = ruleLabelOf(r)
  return {
    desc: withLabel(DESC.amount, label),
    tooltip: `จำนวนเงินที่ผู้ใช้กรอกไว้ใน rule ${r.ruleCode}${label ? ` (${label})` : ''}`,
  }
}
function ruleRateDesc(r) {
  const label = ruleLabelOf(r)
  return {
    desc: withLabel(DESC.rate, label),
    tooltip: `อัตราค่าใช้จ่ายเหมาของตัวเลือกที่ผู้ใช้เลือกไว้ใน rule ${r.ruleCode}${label ? ` (${label})` : ''} — 0 = หักตามจริง`,
  }
}
function formulaRefDesc(ref) {
  const name = formulaNameOf(ref)
  return {
    // No name of its own → NO gloss: the NAME field already shows the code, and the Formula badge
    // already says it is a formula result. Repeating either is the noise this slot exists to remove.
    desc: name ? withLabel(DESC.formula, name) : '',
    tooltip: `ผลลัพธ์ของสูตร${name ? ` “${name}”` : ''} (${ref.outputVar}) ใน Tax Calculate นี้`,
  }
}
function constantDesc(k) {
  const hasValue = k.constValue != null && k.constValue !== ''
  return {
    desc: hasValue ? `${DESC.constant} = ${k.constValue}` : '',
    tooltip: `ค่าคงที่ของเวอร์ชันนี้: ${k.constKey}${hasValue ? ` = ${k.constValue}` : ''} — แก้ค่าที่หน้ารายการสูตร`,
  }
}
function catalogVarDesc(v) {
  return { desc: v.description || '', tooltip: v.description || '' }
}
/**
 * Describe a tax_variable NAME from the LIVE catalog — the view calls this for every block on the
 * canvas (see TaxFormulaBlockly.applyVariableDescriptions), the flyout builders call the same
 * per-kind helpers for the blocks they seed, so the two can never drift apart.
 *
 * Resolution order MIRRORS formulaGraph.classifyField: the gloss and the source badge must agree on
 * what a name IS. A name that resolves to nothing gets an EMPTY description — never a guess.
 * @param {string} name
 * @param {Object} ctx see module doc (rules / outputVars / constants / personalInfo / globals)
 * @returns {{desc:string, tooltip:string}}
 */
export function describeVariable(name, ctx) {
  const n = String(name == null ? '' : name).trim()
  if (!n) return EMPTY_DESC
  const c = ctx || {}
  const lower = n.toLowerCase()

  const konst = normalizeConstants(readCtx(c, 'getConstants', 'constants'))
    .find((k) => k.constKey.toUpperCase() === n.toUpperCase())
  if (konst) return constantDesc(konst)

  const ref = normalizeFormulaRefs(readCtx(c, 'getOutputVars', 'outputVars'))
    .find((r) => r.outputVar.toLowerCase() === lower)
  if (ref) return formulaRefDesc(ref)

  const personal = normalizeVars(c.personalInfo).find((v) => v.name.toLowerCase() === lower)
  if (personal) return catalogVarDesc(personal)
  const global = normalizeVars(c.globals).find((v) => v.name.toLowerCase() === lower)
  if (global) return catalogVarDesc(global)

  const rules = normalizeRules(readCtx(c, 'getRules', 'rules'))
  const amount = rules.find((r) => ruleBridgeVar(r.ruleCode) === lower)
  if (amount) return ruleAmountDesc(amount)
  const rate = rules.find((r) => `${ruleBridgeVar(r.ruleCode)}_rate` === lower)
  if (rate) return ruleRateDesc(rate)

  return EMPTY_DESC
}

// ── dynamic-category flyout builders (used by the registered callbacks) ───────────────

/** OUTPUT_VARS = the "define" category: the new-formula block + sibling output-var chaining refs. */
function defineFlyout(ctx) {
  const items = [labelItem('＋ นิยามสูตรใหม่ (define)'), blockItem(T('tax_define'))]
  const outs = normalizeNames(readCtx(ctx, 'getOutputVars', 'outputVars'))
  if (outs.length) {
    items.push(labelItem('อ้างผลลัพธ์สูตรอื่น (chaining)'))
    outs.forEach((n) => items.push(variableItem(n, SRC.outputVar)))
  } else {
    items.push(labelItem('ยังไม่มีสูตรอื่นให้อ้างอิง'))
  }
  return items
}

/**
 * FORMULA_REFS = every OTHER formula in THIS calc version, offered as an OutputVar reference variable
 * — "ดึงสูตรอื่นมาเป็นตัวแปรในสูตรนี้". The per-formula editor's counterpart of defineFlyout's chaining
 * list (minus the tax_define block, which has no meaning when editing a single expression).
 * Self is excluded so a formula can never reference itself (an instant cycle).
 */
function formulaRefsFlyout(ctx) {
  const self = String(
    (typeof ctx.getSelfOutputVar === 'function' ? ctx.getSelfOutputVar() : ctx.selfOutputVar) || '',
  ).trim().toLowerCase()
  const refs = normalizeFormulaRefs(readCtx(ctx, 'getOutputVars', 'outputVars'))
    .filter((r) => r.outputVar.toLowerCase() !== self)
  if (!refs.length) return [labelItem('ยังไม่มีสูตรอื่นใน Tax Calculate นี้')]
  const items = [labelItem('ลากมาใช้ = ดึงผลลัพธ์ของสูตรนั้นมาเป็นตัวแปร')]
  refs.forEach((r) => {
    // The block already shows the code; a heading is worth its vertical space ONLY when the ชื่อสูตร
    // says something the code does not (a Name backfilled to the OutputVar reads "x · x" — pure noise).
    const name = formulaNameOf(r)
    if (name) items.push(labelItem(name))
    items.push(variableItem(r.outputVar, SRC.outputVar, formulaRefDesc(r).desc))
  })
  return items
}

/** Accept plain strings, {name}, or a formula ref row ({outputVar, label}) — label = the formula's ชื่อสูตร. */
function normalizeFormulaRefs(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((f) => {
      if (!f) return null
      if (typeof f === 'string') return { outputVar: f.trim(), label: '' }
      const outputVar = String(f.outputVar || f.OutputVar || f.name || f.Name || '').trim()
      const label = String(f.label || f.Label || '').trim()
      return { outputVar, label }
    })
    .filter((f) => f && f.outputVar)
}

/** Lower-cased rule-code bridge var (SALARY_40_1 → salary_40_1) — the value entered for that rule. */
function ruleBridgeVar(code) {
  return String(code == null ? '' : code).trim().toLowerCase()
}

/**
 * TAX_RULES flyout. A formula binds to no rule, so there is no "current rule" and no rule-scoped
 * field: every rule value is reached by its own distinctly-named global bridge var, valid in ANY
 * formula. Each rule emits the symmetric pair the engine puts in the bag:
 *   • `salary_40_1`      — the amount entered for that rule
 *   • `salary_40_1_rate` — the เหมา rate of the expense option chosen for that rule (0 = หักตามจริง)
 * So §40(5) is just `income_40_5 * income_40_5_rate` — neutral, nothing bound.
 *
 * …until the rule holds MANY entries, each with its own asset category and therefore its own เหมา rate
 * (S1-P10). "amount × rate" over the rule as a whole is then simply the wrong arithmetic — and the
 * right one is no longer authored here at all: the per-entry data is reached by PATH now
 * (`INCOME_40_5.subItems[].items[].amount`), from the เส้นทางข้อมูล category, and a `[]` path iterates
 * and sums by itself. The Σ block this category used to offer, and the three item_* variables that
 * only meant anything inside it, are RETIRED — offering either would advertise a dead end: the backend
 * refuses both at save AND at run time (NCalcFormulaEvaluator.RetiredFunctions / RetiredIdentifiers).
 *
 * Shape: ONE heading per rule, then its two bridge variables. Each block carries its own gloss, so the
 * flyout stays scannable as a list of RULES rather than a wall of per-block captions.
 */
function taxRulesFlyout(ctx) {
  const rules = normalizeRules(readCtx(ctx, 'getRules', 'rules'))
  if (!rules.length) return [labelItem('ยังไม่มี Tax Rules ในเวอร์ชันนี้')]
  const items = [labelItem('── อ้างค่าของ rule เจาะจง (ชื่อไม่ซ้ำ · ใช้ได้ทุกสูตร) ──')]
  rules.forEach((r) => {
    const bridge = ruleBridgeVar(r.ruleCode)
    if (!bridge) return
    items.push(labelItem(ruleHeading(r)))
    items.push(variableItem(bridge, SRC.ruleInput, ruleAmountDesc(r).desc))
    items.push(variableItem(`${bridge}_rate`, SRC.ruleRate, ruleRateDesc(r).desc))
  })
  items.push(labelItem('รายการย่อยของ rule อยู่ที่หมวด “เส้นทางข้อมูล”'))
  return items
}

/** CONSTANTS = one prefilled tax_constant per version constKey + a blank "new constant". */
function constantsFlyout(ctx) {
  const consts = normalizeConstants(readCtx(ctx, 'getConstants', 'constants'))
  const items = []
  if (consts.length) {
    consts.forEach((c) => items.push(constantItem(c.constKey, c.constValue)))
  } else {
    items.push(labelItem('ยังไม่มีค่าคงที่'))
  }
  items.push(labelItem('＋ ค่าคงที่ใหม่'))
  items.push(blockItem(T('tax_constant'))) // blank — drag to mint a new constant
  return items
}

/**
 * FORMULA_CONSTANTS = the version's EXISTING constants only, as Constant-source variables.
 * Differs from {@link constantsFlyout} on purpose: the per-formula editor must not mint constants
 * (a blank `tax_constant` would compile to an identifier that does not exist in the version → the
 * backend rejects the save). Minting/editing constants belongs to the Formula List (version level).
 * Emitting `tax_variable` (not `tax_constant`) also matches what ncalcToBlocks rebuilds from an
 * expression, so the stored graph and a rebuild stay structurally identical.
 */
function formulaConstantsFlyout(ctx) {
  const consts = normalizeConstants(readCtx(ctx, 'getConstants', 'constants'))
  if (!consts.length) return [labelItem('ยังไม่มีค่าคงที่ — เพิ่มได้ที่หน้ารายการสูตร')]
  const items = [labelItem('ค่าคงที่ของเวอร์ชันนี้ (แก้ค่าที่หน้ารายการสูตร)')]
  // No per-constant caption: the block shows the key and its gloss carries the value.
  consts.forEach((c) => items.push(variableItem(c.constKey, SRC.constant, constantDesc(c).desc)))
  return items
}

/** TAX_SEARCH = filtered union of catalog blocks by ctx.getSearchQuery() (opt-in category). */
function searchFlyout(ctx) {
  const raw = typeof ctx.getSearchQuery === 'function' ? ctx.getSearchQuery() : ctx.searchQuery
  const q = String(raw == null ? '' : raw).trim()
  if (!q) return [labelItem('พิมพ์เพื่อค้นหาบล็อก…')]
  const union = [
    ...taxRulesFlyout(ctx).filter(isBlockItem),
    ...defineFlyout(ctx).filter(isBlockItem),
    ...constantsFlyout(ctx).filter(isBlockItem),
    ...normalizeVars(ctx.personalInfo).map((v) => variableItem(v.name, SRC.personalInfo, v.description)),
    ...normalizeVars(ctx.globals).map((v) => variableItem(v.name, SRC.globals, v.description)),
  ]
  const hits = filterFlyoutItems(union, q)
  return hits.length ? hits : [labelItem('ไม่พบบล็อกที่ค้นหา')]
}

function isBlockItem(it) {
  return it && it.kind === 'block'
}

function flyoutItemText(it) {
  if (!it) return ''
  if (it.kind === 'block') {
    const f = it.fields || {}
    const extra = it.extraState || {}
    // desc is included so searching a Thai rule label ("ค่าเช่า") finds the block it describes —
    // the label is no longer a separate flyout item that could match on its own. SCOPE is the Σ's
    // only NAME-like field: without it, searching a rule code finds that rule's bridge vars but not
    // the Σ seeded from the very same code.
    return `${it.type || ''} ${f.NAME || ''} ${f.CONSTKEY || ''} ${f.SCOPE || ''} ${extra.source || ''} ${extra.desc || ''}`.toLowerCase()
  }
  if (it.kind === 'label' || it.kind === 'button') return String(it.text || '').toLowerCase()
  return ''
}

/**
 * Filter an array of flyout JSON items by a search query (case-insensitive substring over
 * type/NAME/CONSTKEY/source/label). Exposed for a 5-A Vuetify search field.
 * @param {Array<Object>} items
 * @param {string} query
 * @returns {Array<Object>}
 */
export function filterFlyoutItems(items, query) {
  const q = String(query == null ? '' : query).trim().toLowerCase()
  const list = Array.isArray(items) ? items : []
  if (!q) return list.slice()
  return list.filter((it) => flyoutItemText(it).includes(q))
}

// ── category builders ─────────────────────────────────────────────────────────────────
/**
 * Compare category contents — the comparison block, then the text literal it takes as an operand.
 * `tax_text` lives HERE rather than next to `tax_number` in Number because a comparison is the only
 * socket that accepts it (arithmetic rejects String by type check): an author reaches for text while
 * building a condition, and shelving it under Number would advertise a slot it does not fit.
 * Both editors' Compare categories are the same list — one builder, so they cannot drift.
 */
function compareCategoryItems() {
  return [
    blockItem(T('tax_compare'), { fields: { OP: 'LT' } }),
    labelItem('เทียบกับข้อความ (เช่น sex == “FEMALE”)'),
    blockItem(T('tax_text')),
    labelItem('ค่าความจริงคงที่ — ใช้ปิดท้าย IFS'),
    blockItem(T('tax_bool')),
  ]
}

function customCategory(name, styleKey, callbackKey) {
  return { kind: 'category', name, categorystyle: styleKey, custom: callbackKey }
}
function staticVarCategory(name, styleKey, names, source, emptyText) {
  const list = normalizeVars(names)
  const contents = list.length
    ? list.map((v) => variableItem(v.name, source, v.description))
    : [labelItem(emptyText)]
  return { kind: 'category', name, categorystyle: styleKey, contents }
}

/**
 * Build the catalog-driven Blockly toolbox JSON. Dynamic categories (define/Tax Rules/Constants)
 * are `custom` callbacks wired by registerToolboxCallbacks; static categories carry inline contents.
 * Works with an EMPTY ctx — every category still renders.
 * @param {Object} [ctx] see module doc for the ctx shape.
 * @returns {{kind:'categoryToolbox', contents:Array<Object>}}
 */
export function buildToolbox(ctx) {
  const c = ctx || {}
  const contents = [
    // 0. โครงสร้าง (Flow) — START hat + FINAL cap (drag to re-add if deleted)
    {
      kind: 'category',
      name: '▶ โครงสร้าง (START / FINAL)',
      colour: '#43A047',
      contents: [
        labelItem('เริ่ม–จบของสายการคำนวณ'),
        blockItem(T('tax_start')),
        blockItem(T('tax_result')),
      ],
    },
    // 1. สูตรใหม่ (Output/define) — dynamic (new-formula block + output-var chaining refs)
    customCategory('＋ นิยามสูตรใหม่ (define)', CATEGORY_STYLES.define, TOOLBOX_CALLBACK_KEYS.outputVars),
    // 2. Tax Rules — dynamic (per rule: its amount bridge var + that rule's เหมา rate)
    customCategory('Tax Rules', CATEGORY_STYLES.taxRules, TOOLBOX_CALLBACK_KEYS.taxRules),
    // 2b. เส้นทางข้อมูล — the NESTED path blocks (INCOME_40_5 ▸ subItems[] ▸ amount)
    pathCategory('เส้นทางข้อมูล (ทุกรายการ)', c.paths),
    // 3. Personal Data — static (PERSONAL_INFO_FIELDS)
    staticVarCategory('Personal Data', CATEGORY_STYLES.personal, c.personalInfo, SRC.personalInfo, 'ยังไม่มีฟิลด์ข้อมูลส่วนตัว'),
    // 4. Globals — static
    staticVarCategory('Globals', CATEGORY_STYLES.globals, c.globals, SRC.globals, 'ยังไม่มี Globals'),
    // 5. Constants — dynamic (per constKey + new-constant)
    customCategory('Constants', CATEGORY_STYLES.constants, TOOLBOX_CALLBACK_KEYS.constants),
    // 6. Number
    { kind: 'category', name: 'Number', categorystyle: CATEGORY_STYLES.number, contents: [blockItem(T('tax_number'))] },
    // 7. Operators (default OP = ADD; dropdown covers + − × ÷)
    { kind: 'category', name: 'Operators', categorystyle: CATEGORY_STYLES.operators, contents: [blockItem(T('tax_arith'), { fields: { OP: 'ADD' } })] },
    // 8. Compare (default OP = LT; dropdown covers < <= > >= == !=) + the text literal to compare against
    { kind: 'category', name: 'Compare', categorystyle: CATEGORY_STYLES.compare, contents: compareCategoryItems() },
    // 9. Functions
    {
      kind: 'category',
      name: 'Functions',
      categorystyle: CATEGORY_STYLES.functions,
      contents: [
        blockItem(T('tax_func_min')), blockItem(T('tax_func_max')), blockItem(T('tax_func_round')),
        blockItem(T('tax_func_if')), blockItem(T('tax_func_ifs')),
        // Σ sits with the functions because that is where it is NEEDED: the editor refuses a `[]` whose
        // whole iterated computation is wrapped in one of the five above, and offers the two Σ spellings
        // in the refusal. A palette that named the fix without stocking it would be a dead end.
        labelItem('รวมทุกรายการ (ใช้คู่กับเส้นทางข้อมูลที่มี [])'),
        blockItem(T('tax_func_sum')),
      ],
    },
    // 10. Bracket (single, non-compiling)
    { kind: 'category', name: 'Bracket', categorystyle: CATEGORY_STYLES.bracket, contents: [blockItem(T('tax_bracket'))] },
  ]

  // Optional search category (5-A opt-in). Default off so the standard toolbox has exactly 10 categories.
  if (c.includeSearchCategory) {
    contents.unshift(customCategory('🔍 ค้นหา', CATEGORY_STYLES.define, TOOLBOX_CALLBACK_KEYS.search))
  }

  return { kind: 'categoryToolbox', contents }
}

/**
 * Build the toolbox for the PER-FORMULA editor (Formula level) — the workspace holds ONE expression
 * tree, so everything version-level or chain-level is dropped: no START/FINAL spine, no `tax_define`
 * (the ชื่อสูตร / รหัสสูตร / คำอธิบาย live in the Formula editor's header bar), and no `tax_bracket`
 * (one tier table per version, edited in the list).
 *
 * The first category is the headline capability: pull ANOTHER formula of the same Tax Calculate in as
 * a variable. Requires `ctx.getSelfOutputVar()` (or `ctx.selfOutputVar`) so the formula being edited is
 * excluded from its own reference list.
 * @param {Object} [ctx] see module doc (+ selfOutputVar/getSelfOutputVar)
 * @returns {{kind:'categoryToolbox', contents:Array<Object>}}
 */
export function buildFormulaToolbox(ctx) {
  const c = ctx || {}
  const contents = [
    // 0. สูตรอื่นใน Calculate นี้ — dynamic (the "ดึงสูตรอื่นมาเป็นตัวแปร" category)
    customCategory('ƒ สูตรอื่นใน Calculate นี้', CATEGORY_STYLES.define, TOOLBOX_CALLBACK_KEYS.formulaRefs),
    // 1. Tax Rules — dynamic (per rule: its amount bridge var + that rule's เหมา rate)
    customCategory('Tax Rules', CATEGORY_STYLES.taxRules, TOOLBOX_CALLBACK_KEYS.taxRules),
    // 1b. เส้นทางข้อมูล — the NESTED path blocks (INCOME_40_5 ▸ subItems[] ▸ amount)
    pathCategory('เส้นทางข้อมูล (ทุกรายการ)', c.paths),
    // 2. Personal Data — static
    staticVarCategory('Personal Data', CATEGORY_STYLES.personal, c.personalInfo, SRC.personalInfo, 'ยังไม่มีฟิลด์ข้อมูลส่วนตัว'),
    // 3. Globals — static
    staticVarCategory('Globals', CATEGORY_STYLES.globals, c.globals, SRC.globals, 'ยังไม่มี Globals'),
    // 4. Constants — dynamic (existing version constants only; minting lives in the Formula List)
    customCategory('Constants', CATEGORY_STYLES.constants, TOOLBOX_CALLBACK_KEYS.formulaConstants),
    // 5. Number
    { kind: 'category', name: 'Number', categorystyle: CATEGORY_STYLES.number, contents: [blockItem(T('tax_number'))] },
    // 6. Operators
    { kind: 'category', name: 'Operators', categorystyle: CATEGORY_STYLES.operators, contents: [blockItem(T('tax_arith'), { fields: { OP: 'ADD' } })] },
    // 7. Compare (+ the text literal — the only socket it fits is this category's block)
    { kind: 'category', name: 'Compare', categorystyle: CATEGORY_STYLES.compare, contents: compareCategoryItems() },
    // 8. Functions
    {
      kind: 'category',
      name: 'Functions',
      categorystyle: CATEGORY_STYLES.functions,
      contents: [
        blockItem(T('tax_func_min')), blockItem(T('tax_func_max')), blockItem(T('tax_func_round')),
        blockItem(T('tax_func_if')), blockItem(T('tax_func_ifs')),
        // Σ sits with the functions because that is where it is NEEDED: the editor refuses a `[]` whose
        // whole iterated computation is wrapped in one of the five above, and offers the two Σ spellings
        // in the refusal. A palette that named the fix without stocking it would be a dead end.
        labelItem('รวมทุกรายการ (ใช้คู่กับเส้นทางข้อมูลที่มี [])'),
        blockItem(T('tax_func_sum')),
      ],
    },
  ]
  if (c.includeSearchCategory) {
    contents.unshift(customCategory('🔍 ค้นหา', CATEGORY_STYLES.define, TOOLBOX_CALLBACK_KEYS.search))
  }
  return { kind: 'categoryToolbox', contents }
}

/**
 * Register the dynamic-category flyout callbacks on a live Blockly workspace. The callbacks close
 * over `ctx` and read the LIVE catalog (getters preferred) on every flyout open, so newly-added
 * rules/constants/output-vars appear without rebuilding the toolbox.
 * Call AFTER Blockly.inject, BEFORE a dynamic category is opened.
 * @param {Object} workspace a Blockly WorkspaceSvg with registerToolboxCategoryCallback
 * @param {Object} ctx see module doc
 */
export function registerToolboxCallbacks(workspace, ctx) {
  if (!workspace || typeof workspace.registerToolboxCategoryCallback !== 'function') return
  const c = ctx || {}
  workspace.registerToolboxCategoryCallback(TOOLBOX_CALLBACK_KEYS.outputVars, () => defineFlyout(c))
  workspace.registerToolboxCategoryCallback(TOOLBOX_CALLBACK_KEYS.formulaRefs, () => formulaRefsFlyout(c))
  workspace.registerToolboxCategoryCallback(TOOLBOX_CALLBACK_KEYS.taxRules, () => taxRulesFlyout(c))
  workspace.registerToolboxCategoryCallback(TOOLBOX_CALLBACK_KEYS.constants, () => constantsFlyout(c))
  workspace.registerToolboxCategoryCallback(TOOLBOX_CALLBACK_KEYS.formulaConstants, () => formulaConstantsFlyout(c))
  workspace.registerToolboxCategoryCallback(TOOLBOX_CALLBACK_KEYS.search, () => searchFlyout(c))
}

export default {
  buildToolbox,
  buildFormulaToolbox,
  registerToolboxCallbacks,
  filterFlyoutItems,
  describeVariable,
  CATEGORY_STYLES,
  TOOLBOX_CALLBACK_KEYS,
}
